import React, { useEffect, useState } from "react";
import {
  getAccounts, getOrders, deleteAccount, payOrder, cancelOrder, markOverdue,
  CreditAccount, CreditOrder,
} from "../api/credit";
import CreditAccountModal from "../components/credit/CreditAccountModal";
import "../styles/pages/credit-page.css";

const isMobile = () => window.innerWidth <= 768;

type TabType = "accounts" | "orders";

function PayMethodModal({ order, onConfirm, onClose }: {
  order: CreditOrder;
  onConfirm: (method: string) => void;
  onClose: () => void;
}) {
  const [method, setMethod] = useState<'cash' | 'card'>('card');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#111827', borderRadius: '16px 16px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: 480 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ width: 40, height: 4, background: '#374151', borderRadius: 2, margin: '0 auto 20px' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f9fafb', marginBottom: 4 }}>Способ оплаты</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
          Заказ #{order.id} · {Number(order.amount).toLocaleString('ru')} ₸
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          {(['card', 'cash'] as const).map(m => (
            <button key={m} onClick={() => setMethod(m)}
              style={{
                flex: 1, padding: '14px 0', borderRadius: 12, border: `2px solid ${method === m ? '#3b82f6' : '#1f2937'}`,
                background: method === m ? '#1d3a6e' : '#1f2937', color: method === m ? '#93c5fd' : '#9ca3af',
                fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 22 }}>{m === 'card' ? '💳' : '💵'}</span>
              {m === 'card' ? 'Карта' : 'Наличные'}
            </button>
          ))}
        </div>
        <button onClick={() => onConfirm(method)}
          style={{ width: '100%', padding: '14px', borderRadius: 10, background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}>
          Подтвердить оплату
        </button>
        <button onClick={onClose}
          style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'transparent', color: '#6b7280', fontSize: 14, border: 'none', cursor: 'pointer', marginTop: 8 }}>
          Отмена
        </button>
      </div>
    </div>
  );
}

export default function CreditPage() {
  const [tab, setTab] = useState<TabType>("accounts");
  const [accounts, setAccounts] = useState<CreditAccount[]>([]);
  const [orders, setOrders] = useState<CreditOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<"new" | CreditAccount | null>(null);
  const [payModal, setPayModal] = useState<CreditOrder | null>(null);
  const [mob] = useState(isMobile);

  const load = async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([getAccounts(), getOrders()]);
      setAccounts(a);
      setOrders(o);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDeleteAccount = async (id: number, clientName: string) => {
    if (!window.confirm(`Удалить кредитный аккаунт клиента ${clientName}?`)) return;
    try {
      await deleteAccount(id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Ошибка удаления");
    }
  };

  const handlePay = (order: CreditOrder) => setPayModal(order);

  const confirmPay = async (method: string) => {
    if (!payModal) return;
    try {
      await payOrder(payModal.id, method);
      setPayModal(null);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Ошибка");
    }
  };

  const handleCancel = async (order: CreditOrder) => {
    if (!window.confirm(`Отменить кредитный заказ #${order.id}?`)) return;
    try {
      await cancelOrder(order.id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Ошибка");
    }
  };

  const handleMarkOverdue = async () => {
    const res = await markOverdue();
    alert(`Помечено просроченных: ${res.updated}`);
    load();
  };

  const statusLabel = (s: CreditOrder["status"]) => ({
    pending: "Ожидает", paid: "Оплачен", overdue: "Просрочен", cancelled: "Отменён",
  }[s] ?? s);

  const statusColor = (s: CreditOrder["status"]) => ({
    pending: "#f59e0b", paid: "#10b981", overdue: "#ef4444", cancelled: "#6b7280",
  }[s] ?? "#9ca3af");

  // Статистика
  const totalLimit = accounts.reduce((s, a) => s + Number(a.limitAmount), 0);
  const pendingOrders = orders.filter(o => o.status === "pending");
  const overdueOrders = orders.filter(o => o.status === "overdue");
  const usedCredit = [...pendingOrders, ...overdueOrders].reduce((s, o) => s + Number(o.amount), 0);

  return (
    <div className="credit-page">
      {/* Заголовок */}
      <div className="credit-header">
        <div className="credit-title-row">
          <h1 className="credit-title">Кредитные клиенты</h1>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="secondary-btn" onClick={handleMarkOverdue} style={{ fontSize: 12 }}>
              Проверить просрочку
            </button>
            <button className="primary-btn" onClick={() => setModal("new")}>
              + Новый аккаунт
            </button>
          </div>
        </div>

        {/* Статы */}
        <div className="credit-stats">
          <div className="credit-stat">
            <div className="credit-stat-val">{accounts.length}</div>
            <div className="credit-stat-label">Клиентов</div>
          </div>
          <div className="credit-stat">
            <div className="credit-stat-val">{totalLimit.toLocaleString("ru")} ₸</div>
            <div className="credit-stat-label">Общий лимит</div>
          </div>
          <div className="credit-stat">
            <div className="credit-stat-val" style={{ color: "#f59e0b" }}>{usedCredit.toLocaleString("ru")} ₸</div>
            <div className="credit-stat-label">Выдано в долг</div>
          </div>
          <div className="credit-stat">
            <div className="credit-stat-val" style={{ color: overdueOrders.length ? "#ef4444" : "#10b981" }}>
              {overdueOrders.length}
            </div>
            <div className="credit-stat-label">Просрочено</div>
          </div>
        </div>

        {/* Табы */}
        <div className="credit-tabs">
          <button className={`credit-tab ${tab === "accounts" ? "active" : ""}`} onClick={() => setTab("accounts")}>
            Аккаунты ({accounts.length})
          </button>
          <button className={`credit-tab ${tab === "orders" ? "active" : ""}`} onClick={() => setTab("orders")}>
            Заказы ({orders.length})
          </button>
        </div>
      </div>

      {/* Контент */}
      <div className="credit-content">
        {loading ? (
          <div className="credit-empty">Загрузка...</div>
        ) : tab === "accounts" ? (
          <>
            {accounts.length === 0 ? (
              <div className="credit-empty">Нет кредитных аккаунтов. Создайте первый.</div>
            ) : mob ? (
              /* Мобайл — карточки */
              <div className="credit-cards">
                {accounts.map(a => (
                  <div key={a.id} className={`credit-card ${!a.isActive ? "inactive" : ""}`}>
                    <div className="credit-card-top">
                      <div className="credit-card-name">
                        {a.client?.firstName} {a.client?.lastName}
                        {!a.isActive && <span className="credit-badge-off">Неактивен</span>}
                      </div>
                      <div className="credit-card-actions">
                        <button className="table-btn" onClick={() => setModal(a)}>✎</button>
                        <button className="table-btn danger" onClick={() => handleDeleteAccount(a.id, `${a.client?.firstName} ${a.client?.lastName}`)}>✕</button>
                      </div>
                    </div>
                    <div className="credit-card-rows">
                      <div className="credit-card-row">
                        <span>Лимит</span>
                        <strong>{Number(a.limitAmount).toLocaleString("ru")} ₸</strong>
                      </div>
                      <div className="credit-card-row">
                        <span>Срок</span>
                        <strong>{a.paymentTermDays} дн.</strong>
                      </div>
                      {a.pipeline && (
                        <div className="credit-card-row">
                          <span>Воронка</span>
                          <strong>{a.pipeline.name}</strong>
                        </div>
                      )}
                      <div className="credit-card-row">
                        <span>Товары</span>
                        <strong>
                          {a.allowedProductIds?.length ? `${a.allowedProductIds.length} позиций`
                            : a.allowedCategoryIds?.length ? `${a.allowedCategoryIds.length} категорий`
                            : "Все"}
                        </strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Десктоп — таблица */
              <div className="credit-table-wrap">
                <table className="credit-table">
                  <thead>
                    <tr>
                      <th>Клиент</th>
                      <th>Лимит</th>
                      <th>Срок</th>
                      <th>Воронка / Стадия</th>
                      <th>Доступные товары</th>
                      <th>Статус</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(a => (
                      <tr key={a.id} className={!a.isActive ? "row-inactive" : ""}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{a.client?.firstName} {a.client?.lastName}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>{a.client?.phone}</div>
                        </td>
                        <td style={{ fontWeight: 600, color: "#a5b4fc" }}>{Number(a.limitAmount).toLocaleString("ru")} ₸</td>
                        <td>{a.paymentTermDays} дн.</td>
                        <td>
                          {a.pipeline ? (
                            <><div>{a.pipeline.name}</div><div style={{ fontSize: 11, color: "#6b7280" }}>{a.stage?.name}</div></>
                          ) : "—"}
                        </td>
                        <td>
                          {a.allowedProductIds?.length ? `${a.allowedProductIds.length} товаров`
                            : a.allowedCategoryIds?.length ? `${a.allowedCategoryIds.length} категорий`
                            : <span style={{ color: "#10b981" }}>Все</span>}
                        </td>
                        <td>
                          <span style={{
                            padding: "2px 8px", borderRadius: 12, fontSize: 11,
                            background: a.isActive ? "#064e3b" : "#1f2937",
                            color: a.isActive ? "#10b981" : "#6b7280",
                          }}>
                            {a.isActive ? "Активен" : "Выключен"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button className="table-btn" onClick={() => setModal(a)}>✎</button>
                            <button className="table-btn danger" onClick={() => handleDeleteAccount(a.id, `${a.client?.firstName} ${a.client?.lastName}`)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          /* ── Заказы ── */
          <>
            {orders.length === 0 ? (
              <div className="credit-empty">Кредитных заказов пока нет.</div>
            ) : mob ? (
              <div className="credit-cards">
                {orders.map(o => (
                  <div key={o.id} className="credit-card">
                    <div className="credit-card-top">
                      <div className="credit-card-name">
                        {o.client?.firstName} {o.client?.lastName}
                      </div>
                      <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 11, background: "#1a2030", color: statusColor(o.status) }}>
                        {statusLabel(o.status)}
                      </span>
                    </div>
                    <div className="credit-card-rows">
                      <div className="credit-card-row"><span>Сумма</span><strong style={{ color: "#a5b4fc" }}>{Number(o.amount).toLocaleString("ru")} ₸</strong></div>
                      <div className="credit-card-row"><span>До</span><strong>{o.dueDate}</strong></div>
                      {o.deal && <div className="credit-card-row"><span>Сделка</span><strong>{o.deal.number}</strong></div>}
                    </div>
                    {o.status === "pending" && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button className="primary-btn" style={{ flex: 1, fontSize: 12, padding: "6px" }} onClick={() => handlePay(o)}>Оплачен</button>
                        <button className="secondary-btn" style={{ flex: 1, fontSize: 12, padding: "6px" }} onClick={() => handleCancel(o)}>Отменить</button>
                      </div>
                    )}
                    {o.status === "overdue" && (
                      <button className="primary-btn" style={{ width: "100%", fontSize: 12, padding: "6px", marginTop: 8, background: "#7f1d1d" }} onClick={() => handlePay(o)}>Отметить оплаченным</button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="credit-table-wrap">
                <table className="credit-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Клиент</th>
                      <th>Сумма</th>
                      <th>Срок оплаты</th>
                      <th>Сделка</th>
                      <th>Создан</th>
                      <th>Статус</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id}>
                        <td style={{ color: "#6b7280", fontSize: 12 }}>#{o.id}</td>
                        <td>{o.client?.firstName} {o.client?.lastName}</td>
                        <td style={{ fontWeight: 600, color: "#a5b4fc" }}>{Number(o.amount).toLocaleString("ru")} ₸</td>
                        <td style={{ color: o.status === "overdue" ? "#ef4444" : "#f9fafb" }}>{o.dueDate}</td>
                        <td>{o.deal ? <span style={{ color: "#818cf8" }}>{o.deal.number}</span> : "—"}</td>
                        <td style={{ fontSize: 12, color: "#6b7280" }}>{new Date(o.createdAt).toLocaleDateString("ru")}</td>
                        <td>
                          <span style={{
                            padding: "2px 8px", borderRadius: 12, fontSize: 11,
                            background: "#1a2030", color: statusColor(o.status),
                          }}>
                            {statusLabel(o.status)}
                          </span>
                        </td>
                        <td>
                          {(o.status === "pending" || o.status === "overdue") && (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="table-btn" style={{ color: "#10b981" }} onClick={() => handlePay(o)}>✓ Оплачен</button>
                              {o.status === "pending" && (
                                <button className="table-btn danger" onClick={() => handleCancel(o)}>Отменить</button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Модалка */}
      {modal && (
        <CreditAccountModal
          account={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
      {payModal && (
        <PayMethodModal
          order={payModal}
          onConfirm={confirmPay}
          onClose={() => setPayModal(null)}
        />
      )}
    </div>
  );
}
